import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BusinessContext = createContext();

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within BusinessProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BusinessProvider = ({ children }) => {
  const [business, setBusiness] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBusiness = async (slug) => {
    try {
      setLoading(true);
      setError(null);
      
      const businessRes = await axios.get(`${API}/business/${slug}`);
      setBusiness(businessRes.data);
      applyTheme(businessRes.data);

      // Load secondary data independently - don't block if one fails
      const [categoriesRes, productsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/business/${slug}/categories`).catch(() => ({ data: [] })),
        axios.get(`${API}/business/${slug}/products`).catch(() => ({ data: [] })),
        axios.get(`${API}/business/${slug}/settings`).catch(() => ({ data: null }))
      ]);

      setCategories(categoriesRes.data || []);
      setProducts(productsRes.data || []);
      setSettings(settingsRes.data);
      
      setLoading(false);
      return businessRes.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Business not found');
      setLoading(false);
      return null;
    }
  };

  const applyTheme = (businessData) => {
    const root = document.documentElement;
    
    // Convert hex to HSL for CSS variables
    const hexToHSL = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return '0 0% 0%';
      
      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;
      
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      
      h = Math.round(h * 360);
      s = Math.round(s * 100);
      l = Math.round(l * 100);
      
      return `${h} ${s}% ${l}%`;
    };
    
    root.style.setProperty('--primary', hexToHSL(businessData.primary_color));
    root.style.setProperty('--accent', hexToHSL(businessData.accent_color));
    
    // Apply custom colors if set
    if (businessData.background_color) root.style.setProperty('--background-custom', businessData.background_color);
    if (businessData.text_color) root.style.setProperty('--text-custom', businessData.text_color);
    if (businessData.card_color) root.style.setProperty('--card-custom', businessData.card_color);
    
    // Apply font family - use explicitly set fonts first, then fall back to theme preset
    let headingFont = businessData.font_heading;
    let bodyFont = businessData.font_body;
    
    if (!headingFont || !bodyFont) {
      if (businessData.theme_preset === 'elegant') {
        headingFont = headingFont || 'Playfair Display';
        bodyFont = bodyFont || 'Poppins';
      } else {
        headingFont = headingFont || 'Montserrat';
        bodyFont = bodyFont || 'Roboto';
      }
    }
    
    root.style.setProperty('--font-heading', `${headingFont}, serif`);
    root.style.setProperty('--font-body', `${bodyFont}, sans-serif`);
    
    // Inject custom fonts stylesheet
    injectCustomFonts();
  };

  const injectCustomFonts = async () => {
    try {
      const response = await axios.get(`${API}/fonts/stylesheet`);
      const { css } = response.data;
      if (css) {
        let styleEl = document.getElementById('custom-fonts-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'custom-fonts-style';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = css;
      }
    } catch (err) {
      // Silent fail - custom fonts are optional
    }
  };

  const clearBusiness = () => {
    setBusiness(null);
    setCategories([]);
    setProducts([]);
    setSettings(null);
    setError(null);
  };

  const refreshData = async () => {
    if (business) {
      await loadBusiness(business.slug);
    }
  };

  // Helper para verificar si el negocio está abierto
  const isBusinessOpen = () => {
    if (!settings?.business_hours) return true;
    
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[now.getDay()];
    const todayHours = settings.business_hours.find(h => h.day === today);
    
    if (!todayHours || !todayHours.is_open) return false;
    
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    // Check main range
    const openTime = parseInt((todayHours.open_time || '00:00').replace(':', ''));
    const closeTime = parseInt((todayHours.close_time || '23:59').replace(':', ''));
    if (currentTime >= openTime && currentTime <= closeTime) return true;
    
    // Check extra ranges
    if (todayHours.extra_ranges?.length > 0) {
      return todayHours.extra_ranges.some(range => {
        const rOpen = parseInt((range.open_time || '00:00').replace(':', ''));
        const rClose = parseInt((range.close_time || '23:59').replace(':', ''));
        return currentTime >= rOpen && currentTime <= rClose;
      });
    }
    
    return false;
  };

  // Helper para obtener horario de hoy
  const getTodayHours = () => {
    if (!settings?.business_hours) return null;
    
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    return settings.business_hours.find(h => h.day === today);
  };

  return (
    <BusinessContext.Provider
      value={{
        business,
        categories,
        products,
        settings,
        loading,
        error,
        loadBusiness,
        clearBusiness,
        refreshData,
        isBusinessOpen,
        getTodayHours
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};
